import cn from 'classnames';
// Ui3
import Text from 'ui3/Text/Text';
import Button from 'ui3/Button/Button';
// Types
import { MatchupFields as Matchup } from 'api/matchup/types/MatchupFields';
import { LiveMatchState, MatchupCorner } from './fightCardTypes';
// Helpers
import {
  formatRoundLabel,
  formatTimer,
  getFighterAName,
  getFighterBName,
  getRoundDurationSeconds,
} from './fightCardHelpers';
// Styles
import styles from './FightControls.module.scss';

export type FightControlsProps = {
  matchups: Matchup[];
  state: LiveMatchState;
  canControl: boolean;
  isHidden: boolean;
  onToggleHidden: () => void;
  onStartRound: (matchupId: string) => void;
  onPauseRound: () => void;
  onResetRound: () => void;
  onClearFight: () => void;
  onSelectRound: (round: number) => void;
  onNextRound: () => void;
  onPrevRound: () => void;
  onDeclareWinner: (matchupId: string, corner: MatchupCorner | null) => void;
};

const Avatar = ({ name }: { name: string }) => (
  <span className={styles.avatar}>
    {name.trim().charAt(0).toUpperCase() || '?'}
  </span>
);

const WinnerBadge = () => <span className={styles.winnerBadge}>Winner</span>;

const FightControls = ({
  matchups,
  state,
  canControl,
  isHidden,
  onToggleHidden,
  onStartRound,
  onPauseRound,
  onResetRound,
  onClearFight,
  onSelectRound,
  onNextRound,
  onPrevRound,
  onDeclareWinner,
}: FightControlsProps) => {
  const orderedMatchups = [...matchups].sort((a, b) => a.order - b.order);

  if (orderedMatchups.length === 0) {
    return (
      <Text variant="body3Regular" color="light-medium">
        No matchups on the card.
      </Text>
    );
  }

  return (
    <ol className={styles.list}>
      {orderedMatchups.map((matchup, index) => {
        const isLive = matchup.id === state.currentMatchupId;
        const duration = getRoundDurationSeconds(matchup);
        const totalRounds = matchup.rounds ?? null;
        const roundOver = isLive && state.roundTimerSeconds >= duration;
        const fightOver =
          roundOver && !!totalRounds && state.currentRound >= totalRounds;
        const remaining = duration - state.roundTimerSeconds;
        const winnerCorner = isLive ? state.winnerCorner : matchup.winnerCorner;
        const fighterAName = getFighterAName(matchup);
        const fighterBName = getFighterBName(matchup);

        return (
          <li
            key={matchup.id}
            className={cn(styles.bout, { [styles.boutLive]: isLive })}
          >
            <div className={styles.boutHeader}>
              <span className={styles.boutTitle}>Bout {index + 1}</span>
              {matchup.isMainEvent && (
                <span className={styles.mainEventBadge}>Main Event</span>
              )}
              {isLive ? (
                isHidden ? (
                  <span className={styles.hiddenBadge}>Hidden</span>
                ) : (
                  <span className={styles.liveBadge}>Live</span>
                )
              ) : (
                <span className={styles.rounds}>
                  {totalRounds ? `${totalRounds} Rounds` : null}
                </span>
              )}
            </div>

            <div className={styles.fighters}>
              <span className={styles.fighter}>
                <Avatar name={fighterAName} />
                <span className={styles.fighterName}>{fighterAName}</span>
                {winnerCorner === MatchupCorner.A && <WinnerBadge />}
              </span>
              <span className={styles.vs}>VS</span>
              <span className={cn(styles.fighter, styles.fighterRight)}>
                {winnerCorner === MatchupCorner.B && <WinnerBadge />}
                <span className={styles.fighterName}>{fighterBName}</span>
                <Avatar name={fighterBName} />
              </span>
            </div>

            {isLive && (
              <div className={styles.clockRow}>
                {canControl && totalRounds ? (
                  <span className={styles.roundChips}>
                    {Array.from({ length: totalRounds }, (_, i) => i + 1).map(
                      (round) => (
                        <button
                          key={round}
                          type="button"
                          className={cn(styles.roundChip, {
                            [styles.roundChipActive]:
                              round === state.currentRound,
                          })}
                          aria-label={`Round ${round}`}
                          onClick={() => onSelectRound(round)}
                        >
                          {round}
                        </button>
                      )
                    )}
                  </span>
                ) : (
                  <span className={styles.roundStepper}>
                    {canControl && (
                      <button
                        type="button"
                        className={styles.roundStepButton}
                        aria-label="Previous round"
                        disabled={state.currentRound <= 1}
                        onClick={onPrevRound}
                      >
                        −
                      </button>
                    )}
                    <span className={styles.roundLabel}>
                      {formatRoundLabel(state.currentRound, totalRounds)}
                    </span>
                    {canControl && (
                      <button
                        type="button"
                        className={styles.roundStepButton}
                        aria-label="Next round"
                        disabled={
                          !!totalRounds && state.currentRound >= totalRounds
                        }
                        onClick={onNextRound}
                      >
                        +
                      </button>
                    )}
                  </span>
                )}
                <span
                  className={cn(styles.timer, {
                    [styles.timerRunning]: state.isTimerRunning,
                  })}
                >
                  {formatTimer(remaining)}
                </span>
              </div>
            )}

            {canControl && (
              <div className={styles.actions}>
                {isLive && state.isTimerRunning ? (
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={onPauseRound}
                  >
                    Pause Round
                  </Button>
                ) : (
                  <Button
                    variant="success"
                    size="small"
                    disabled={fightOver}
                    onClick={() => onStartRound(matchup.id)}
                  >
                    {fightOver ? 'Fight Complete' : 'Start Round'}
                  </Button>
                )}
                {isLive && (
                  <>
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={onResetRound}
                    >
                      Reset Round
                    </Button>
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={onToggleHidden}
                    >
                      {isHidden ? 'Show Scoreboard' : 'Hide Scoreboard'}
                    </Button>
                    <Button variant="error" size="small" onClick={onClearFight}>
                      Clear Fight
                    </Button>
                  </>
                )}
              </div>
            )}

            {canControl && (
              <div className={styles.actions}>
                {winnerCorner ? (
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => onDeclareWinner(matchup.id, null)}
                  >
                    Undo Winner
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="primary"
                      size="small"
                      onClick={() =>
                        onDeclareWinner(matchup.id, MatchupCorner.A)
                      }
                    >
                      Winner: {fighterAName}
                    </Button>
                    <Button
                      variant="primary"
                      size="small"
                      onClick={() =>
                        onDeclareWinner(matchup.id, MatchupCorner.B)
                      }
                    >
                      Winner: {fighterBName}
                    </Button>
                  </>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
};

export default FightControls;
